import { cast, flow, SnapshotIn, toGenerator, types } from 'mobx-state-tree';
import { Client, CombinedError } from 'urql';

import { getUrqlClient } from 'stores/env';
import {
  OrgCreateDocument,
  OrgCreateMutation,
  OrgCreateMutationVariables,
} from 'generated/graphql';

interface CreateOrgInput {
  ownerId: string;
  name: string;
  slug?: string;
}

export const UserOrg = types.model('UserOrg', {
  id: types.identifier,
  slug: types.string,
  name: types.string,
  isActive: types.boolean,
});
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IUserOrg extends SnapshotIn<typeof UserOrg> {}

export const OrgStore = types
  .model('OrgStore', {
    orgs: types.array(UserOrg),
    selectedOrgId: types.maybe(types.string),
  })
  .actions((self) => ({
    setOrgs(orgs: IUserOrg[]) {
      self.orgs = cast(orgs);
    },
    setSelectedOrg(id: string) {
      self.selectedOrgId = id;
    },
    createOrg: flow(function* (input: CreateOrgInput) {
      const { ownerId, name, slug } = input;
      const urqlClient = getUrqlClient(self);
      const { org, error } = yield* toGenerator(createOrg(urqlClient, ownerId, name, slug));
      if (error != null) {
        yield Promise.reject(error);
        return undefined;
      }
      if (org != null) {
        self.orgs.unshift(org);
      }
      return org;
    }),
  }))
  .views((self) => ({
    get selectedOrg() {
      return self.orgs.find((org) => org.id === self.selectedOrgId);
    },
  }));

export const createOrgStore = () => {
  return OrgStore.create({});
};
export type TOrgStore = ReturnType<typeof createOrgStore>;

export async function createOrg(
  urqlClient: Client,
  ownerId: string,
  name: string,
  slug?: string,
): Promise<{
  org: OrgCreateMutation['orgCreate'];
  error: CombinedError | undefined;
}> {
  const { data, error } = await urqlClient
    .mutation<OrgCreateMutation, OrgCreateMutationVariables>(OrgCreateDocument, {
      input: {
        name,
        slug,
        ownerId,
      },
    })
    .toPromise();
  const org =
    data != null && data.orgCreate != null
      ? {
          id: data.orgCreate.id,
          name: data.orgCreate.name,
          slug: data.orgCreate.slug,
          isActive: data.orgCreate.isActive,
        }
      : undefined;
  return { org, error };
}
